import {
  Camera,
  Captions,
  CirclePause,
  Mic,
  MicOff,
  PhoneOff,
  Play,
  Radio,
  Send,
  Sparkles,
  Volume2,
  Wand2
} from "lucide-react";
import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import {
  createInitialCallSession,
  reduceCallEvent,
  speakingStateForEmotion,
  type CallSession,
  type Emotion,
  type VisualState
} from "./services/conversation/state-machine";
import { normalizeCharacterManifest, getPlayableAsset } from "./services/media/manifest";
import type { CharacterManifest } from "./services/media/types";
import { RemoteCharacterVideo } from "./components/RemoteCharacterVideo";
import { StateRail } from "./components/StateRail";
import { LocalPreview } from "./components/LocalPreview";

type ChatResponse = {
  text: string;
  emotion: Emotion;
  gesture: string;
  subtitle: string;
  provider?: string;
  model?: string;
};

type ChatHistoryMessage = {
  role: "user" | "assistant";
  content: string;
};

type VideoPreloadItem = {
  state: VisualState;
  src: string;
  fallback?: string;
};

const defaultPrompt = "你刚刚在玩什么游戏？";
const accessTokenStorageKey = "live-frame-access-token";
const allowBrowserTtsFallback = import.meta.env.DEV;
const visualStates: VisualState[] = [
  "idle",
  "listening",
  "thinking",
  "speaking-neutral",
  "speaking-happy",
  "speaking-soft",
  "interrupted"
];

export function App() {
  const [manifest, setManifest] = useState<CharacterManifest | null>(null);
  const [session, setSession] = useState<CallSession>(() => createInitialCallSession());
  const [input, setInput] = useState(defaultPrompt);
  const [isMicOn, setIsMicOn] = useState(false);
  const [isCameraOn, setIsCameraOn] = useState(false);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [transportStatus, setTransportStatus] = useState("待机");
  const [ttsStatus, setTtsStatus] = useState("ElevenLabs 检查中");
  const [llmStatus, setLlmStatus] = useState("LLM 未检查");
  const [isElevenLabsReady, setIsElevenLabsReady] = useState(false);
  const [isTtsConfigChecked, setIsTtsConfigChecked] = useState(false);
  const [accessToken, setAccessToken] = useState(() => readStoredAccessToken());
  const [accessInput, setAccessInput] = useState(() => readStoredAccessToken());
  const [authStatus, setAuthStatus] = useState({
    checked: false,
    required: false,
    authenticated: true
  });
  const [authError, setAuthError] = useState("");
  const [latencyMs, setLatencyMs] = useState<number | null>(null);
  const [audioLevel, setAudioLevel] = useState(0);
  const [ttsMouthLevel, setTtsMouthLevel] = useState(0);
  const chatHistoryRef = useRef<ChatHistoryMessage[]>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const ttsMeterCleanupRef = useRef<(() => void) | null>(null);
  const visibleStateRef = useRef<VisualState>("idle");
  const visualStateWaitersRef = useRef<Map<VisualState, Set<() => void>>>(new Map());
  const isUnlocked = authStatus.checked && (!authStatus.required || authStatus.authenticated);

  useEffect(() => {
    void checkAccessToken(accessToken);
  }, []);

  useEffect(() => {
    if (!isUnlocked) return;

    apiFetch("/api/assets/manifest/neko-gamer")
      .then((response) => response.json())
      .then((data: CharacterManifest) => setManifest(normalizeCharacterManifest(data)))
      .catch(() => setTransportStatus("manifest 加载失败"));

    apiFetch("/api/tts/elevenlabs/config")
      .then((response) => response.json())
      .then((config: { ready: boolean }) => {
        setIsTtsConfigChecked(true);
        setIsElevenLabsReady(config.ready);
        setTtsStatus(config.ready ? "ElevenLabs 可用" : allowBrowserTtsFallback ? "本地语音待命" : "ElevenLabs 未配置");
      })
      .catch(() => {
        setIsTtsConfigChecked(true);
        setIsElevenLabsReady(false);
        setTtsStatus(allowBrowserTtsFallback ? "本地语音待命" : "ElevenLabs 配置失败");
      });

    apiFetch("/api/chat/config")
      .then((response) => response.json())
      .then((config: { ready: boolean; provider: string; model: string }) => {
        setLlmStatus(config.ready ? `${config.provider} ${config.model}` : "LLM 未配置");
      })
      .catch(() => setLlmStatus("LLM 未连接"));
  }, [isUnlocked, accessToken]);

  useEffect(() => {
    if (!localStream || !isMicOn) {
      setAudioLevel(0);
      return;
    }

    const audioContext = new AudioContext();
    const analyser = audioContext.createAnalyser();
    const source = audioContext.createMediaStreamSource(localStream);
    const data = new Uint8Array(analyser.frequencyBinCount);
    let frame = 0;
    analyser.fftSize = 512;
    source.connect(analyser);

    const tick = () => {
      analyser.getByteFrequencyData(data);
      const average = data.reduce((sum, value) => sum + value, 0) / data.length;
      setAudioLevel(Math.min(1, average / 96));
      frame = requestAnimationFrame(tick);
    };

    tick();
    return () => {
      cancelAnimationFrame(frame);
      source.disconnect();
      void audioContext.close();
    };
  }, [localStream, isMicOn]);

  const playableAsset = useMemo(() => {
    if (!manifest) return null;
    return getPlayableAsset(manifest, session.visualState);
  }, [manifest, session.visualState]);

  const preloadVideos = useMemo<VideoPreloadItem[]>(() => {
    if (!manifest) return [];

    return visualStates.flatMap((state) => {
      const asset = manifest.states[state];
      if (!asset?.src?.trim()) return [];
      return [
        {
          state,
          src: asset.src,
          fallback: asset.fallback
        }
      ];
    });
  }, [manifest]);

  async function toggleCamera() {
    if (isCameraOn) {
      localStream?.getTracks().forEach((track) => track.stop());
      setLocalStream(null);
      setIsCameraOn(false);
      setIsMicOn(false);
      return;
    }

    const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
    setLocalStream(stream);
    setIsCameraOn(true);
    setIsMicOn(true);
  }

  function dispatch(event: Parameters<typeof reduceCallEvent>[1]) {
    setSession((current) => reduceCallEvent(current, event));
  }

  function markVisualStateVisible(state: VisualState) {
    visibleStateRef.current = state;
    const waiters = visualStateWaitersRef.current.get(state);
    if (!waiters) return;

    visualStateWaitersRef.current.delete(state);
    waiters.forEach((resolve) => resolve());
  }

  function waitForVisualState(state: VisualState, timeoutMs = 520) {
    if (visibleStateRef.current === state) return Promise.resolve();

    return new Promise<void>((resolve) => {
      const waiters = visualStateWaitersRef.current.get(state) ?? new Set<() => void>();
      visualStateWaitersRef.current.set(state, waiters);

      const timeout = window.setTimeout(() => {
        waiters.delete(done);
        if (waiters.size === 0) visualStateWaitersRef.current.delete(state);
        resolve();
      }, timeoutMs);

      const done = () => {
        window.clearTimeout(timeout);
        resolve();
      };

      waiters.add(done);
    });
  }

  function apiFetch(input: RequestInfo | URL, init: RequestInit = {}) {
    const headers = new Headers(init.headers);
    if (accessToken) {
      headers.set("x-app-access-token", accessToken);
    }

    return fetch(input, {
      ...init,
      headers
    });
  }

  async function checkAccessToken(token: string) {
    try {
      const headers = new Headers();
      if (token) {
        headers.set("x-app-access-token", token);
      }

      const response = await fetch("/api/auth/status", { headers });
      if (!response.ok) throw new Error("auth status unavailable");

      const status = (await response.json()) as {
        required: boolean;
        authenticated: boolean;
      };

      setAuthStatus({
        checked: true,
        required: status.required,
        authenticated: status.authenticated
      });

      if (!status.required || status.authenticated) {
        setAuthError("");
        writeStoredAccessToken(token);
        return true;
      }

      clearStoredAccessToken();
      setAuthError(token ? "访问口令不正确。" : "");
      return false;
    } catch {
      setAuthStatus({
        checked: true,
        required: false,
        authenticated: true
      });
      setAuthError("");
      return true;
    }
  }

  async function handleAccessSubmit(event: FormEvent) {
    event.preventDefault();
    const token = accessInput.trim();
    setAccessToken(token);
    await checkAccessToken(token);
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const text = input.trim();
    if (!text || session.isSpeaking) return;

    const started = performance.now();
    setLatencyMs(null);
    setTransportStatus("用户输入中");
    dispatch({ type: "user_speech_started" });

    let audioUrl = "";

    try {
      await wait(260);
      dispatch({ type: "user_speech_ended", transcript: text });
      setTransportStatus("角色思考中");

      setLlmStatus((current) => (current.includes("未") ? "请求 LLM" : current));

      const response = await apiFetch("/api/chat", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          text,
          history: chatHistoryRef.current
        })
      });

      if (!response.ok) {
        if (response.status === 401) {
          setAuthStatus({ checked: true, required: true, authenticated: false });
          clearStoredAccessToken();
        }
        const payload = (await response.json().catch(() => null)) as { error?: string; detail?: string } | null;
        throw new Error(payload?.detail || payload?.error || "LLM 请求失败");
      }

      const chat = (await response.json()) as ChatResponse;
      chatHistoryRef.current = [
        ...chatHistoryRef.current,
        { role: "user" as const, content: text },
        { role: "assistant" as const, content: chat.subtitle }
      ].slice(-8);

      dispatch({
        type: "llm_response_ready",
        responseText: chat.subtitle,
        emotion: chat.emotion
      });
      setLatencyMs(Math.round(performance.now() - started));
      setLlmStatus(`${chat.provider || "LLM"} ${chat.model || ""}`.trim());
      await playElevenLabsOrFallback(chat.subtitle, chat.emotion);
    } catch (error) {
      const message = error instanceof Error ? error.message : "请求失败";
      dispatch({ type: "error", message });
      setTransportStatus(message);
  }
}

function wait(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function createAbortError() {
  return new DOMException("Audio playback aborted", "AbortError");
}

  async function playElevenLabsOrFallback(text: string, emotion: Emotion) {
    stopCurrentAudio();
    abortRef.current = new AbortController();
    const abortSignal = abortRef.current.signal;

    if (!isTtsConfigChecked) {
      setTtsStatus("ElevenLabs 检查中");
      setTransportStatus("TTS 配置检查中");
      return;
    }

    if (!isElevenLabsReady) {
      if (!allowBrowserTtsFallback) {
        setTtsStatus("ElevenLabs 未就绪");
        setTransportStatus("ElevenLabs 未就绪");
        dispatch({ type: "error", message: "ElevenLabs 未就绪" });
        return;
      }
      setTtsStatus("本地语音降级");
      await speakWithBrowserFallback(text, emotion);
      return;
    }

    setTtsStatus("请求 ElevenLabs");
    let audioUrl = "";

    try {
      const targetVisualState = speakingStateForEmotion(emotion);
      const response = await apiFetch("/api/tts/elevenlabs", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ text, emotion }),
        signal: abortSignal
      });

      if (!response.ok) {
        if (response.status === 401) {
          setAuthStatus({ checked: true, required: true, authenticated: false });
          clearStoredAccessToken();
        }
        const payload = (await response.json().catch(() => null)) as { error?: string } | null;
        throw new Error(payload?.error || "ElevenLabs TTS 失败");
      }

      const blob = await response.blob();
      audioUrl = URL.createObjectURL(blob);
      const audio = new Audio(audioUrl);
      audio.preload = "auto";
      audioRef.current = audio;
      audio.load();
      setTtsStatus("ElevenLabs 准备播放");
      setTransportStatus("角色准备说话");
      dispatch({ type: "tts_audio_started", responseText: text, emotion });
      await waitForVisualState(targetVisualState);
      if (abortSignal.aborted) throw createAbortError();
      await wait(70);
      if (abortSignal.aborted) throw createAbortError();
      ttsMeterCleanupRef.current = startTtsMouthMeter(audio);
      setTtsStatus("ElevenLabs 播放中");
      setTransportStatus("角色说话中");

      await new Promise<void>((resolve, reject) => {
        const cleanup = () => {
          abortSignal.removeEventListener("abort", onAbort);
        };
        const onAbort = () => {
          cleanup();
          reject(createAbortError());
        };
        abortSignal.addEventListener("abort", onAbort, { once: true });
        audio.onended = () => {
          cleanup();
          resolve();
        };
        audio.onerror = () => {
          cleanup();
          reject(new Error("音频播放失败"));
        };
        void audio.play().catch(reject);
      });

      URL.revokeObjectURL(audioUrl);
      stopTtsMouthMeter();
      setTtsStatus("ElevenLabs 播放完成");
      setTransportStatus("待机");
      dispatch({ type: "tts_audio_done" });
    } catch (error) {
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      stopTtsMouthMeter();
      if ((error as Error).name === "AbortError") return;
      if (!allowBrowserTtsFallback) {
        const message = error instanceof Error ? error.message : "ElevenLabs TTS 失败";
        setTtsStatus(`ElevenLabs 失败：${message.slice(0, 24)}`);
        setTransportStatus("ElevenLabs 播放失败");
        dispatch({ type: "error", message });
        return;
      }
      setTtsStatus("本地语音降级");
      await speakWithBrowserFallback(text, emotion);
    }
  }

  async function speakWithBrowserFallback(text: string, emotion: Emotion) {
    setTransportStatus("角色说话中");
    dispatch({ type: "tts_audio_started", responseText: text, emotion });
    ttsMeterCleanupRef.current = startSyntheticMouthMotion(emotion);

    try {
      if ("speechSynthesis" in window) {
        window.speechSynthesis.cancel();
        await new Promise<void>((resolve) => {
          const utterance = new SpeechSynthesisUtterance(text);
          const timeout = window.setTimeout(resolve, Math.max(2200, text.length * 110));
          utterance.lang = "zh-CN";
          utterance.rate = emotion === "happy" ? 1.08 : emotion === "soft" ? 0.92 : 1;
          utterance.pitch = emotion === "happy" ? 1.18 : 1.04;
          utterance.onend = () => {
            window.clearTimeout(timeout);
            resolve();
          };
          utterance.onerror = () => {
            window.clearTimeout(timeout);
            resolve();
          };
          window.speechSynthesis.speak(utterance);
        });
      } else {
        await new Promise((resolve) => window.setTimeout(resolve, Math.max(1800, text.length * 95)));
      }
    } finally {
      stopTtsMouthMeter();
    }

    setTransportStatus("待机");
    dispatch({ type: "tts_audio_done" });
  }

  function stopCurrentAudio() {
    abortRef.current?.abort();
    stopTtsMouthMeter();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    if ("speechSynthesis" in window) {
      window.speechSynthesis.cancel();
    }
  }

  function startTtsMouthMeter(audio: HTMLAudioElement) {
    stopTtsMouthMeter();

    const audioContext = new AudioContext();
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 512;
    const source = audioContext.createMediaElementSource(audio);
    const data = new Uint8Array(analyser.fftSize);
    let frame = 0;
    let smoothed = 0;
    let lastPublished = 0;

    source.connect(analyser);
    analyser.connect(audioContext.destination);
    void audioContext.resume();

    const tick = () => {
      analyser.getByteTimeDomainData(data);
      let sum = 0;
      for (const value of data) {
        const normalized = (value - 128) / 128;
        sum += normalized * normalized;
      }

      const rms = Math.sqrt(sum / data.length);
      const level = Math.min(1, Math.max(0, (rms - 0.012) * 7.8));
      smoothed = smoothed * 0.58 + level * 0.42;

      if (Math.abs(smoothed - lastPublished) > 0.018 || smoothed < 0.03) {
        lastPublished = smoothed;
        setTtsMouthLevel(smoothed < 0.025 ? 0 : smoothed);
      }

      frame = requestAnimationFrame(tick);
    };

    tick();

    return () => {
      cancelAnimationFrame(frame);
      source.disconnect();
      analyser.disconnect();
      void audioContext.close();
      setTtsMouthLevel(0);
    };
  }

  function stopTtsMouthMeter() {
    ttsMeterCleanupRef.current?.();
    ttsMeterCleanupRef.current = null;
    setTtsMouthLevel(0);
  }

  function startSyntheticMouthMotion(emotion: Emotion) {
    let frame = 0;
    const started = performance.now();
    const intensity = emotion === "happy" ? 0.82 : emotion === "soft" ? 0.48 : 0.64;

    const tick = () => {
      const elapsed = performance.now() - started;
      const wave = Math.sin(elapsed / 74) * 0.5 + Math.sin(elapsed / 137) * 0.28 + 0.5;
      setTtsMouthLevel(Math.max(0.08, Math.min(1, wave * intensity)));
      frame = requestAnimationFrame(tick);
    };

    tick();

    return () => {
      cancelAnimationFrame(frame);
      setTtsMouthLevel(0);
    };
  }

  function interrupt() {
    stopCurrentAudio();
    setTtsStatus("已打断");
    setTransportStatus("用户打断");
    dispatch({ type: "user_interrupt" });
  }

  function selectState(state: VisualState) {
    setSession((current) => ({
      ...current,
      visualState: state,
      isSpeaking: state.startsWith("speaking")
    }));
  }

  if (!authStatus.checked) {
    return (
      <main className="auth-shell">
        <section className="auth-card">
          <span className="brand-mark">
            <Radio size={18} />
          </span>
          <h1>Live Frame</h1>
          <p>正在检查访问权限。</p>
        </section>
      </main>
    );
  }

  if (authStatus.required && !authStatus.authenticated) {
    return (
      <main className="auth-shell">
        <form className="auth-card" onSubmit={handleAccessSubmit}>
          <span className="brand-mark">
            <Radio size={18} />
          </span>
          <h1>Live Frame</h1>
          <p>输入访问口令后开始角色视频通话。</p>
          <input
            value={accessInput}
            onChange={(event) => setAccessInput(event.target.value)}
            placeholder="访问口令"
            type="password"
            autoFocus
          />
          {authError ? <span className="auth-error">{authError}</span> : null}
          <button type="submit" className="primary-action" disabled={!accessInput.trim()}>
            <Send size={18} />
            进入
          </button>
        </form>
      </main>
    );
  }

  return (
    <main className="app-shell">
      <VideoPreloadCache videos={preloadVideos} />
      <section className="call-stage" aria-label="角色视频通话">
        <div className="stage-topbar">
          <div className="brand-lockup">
            <span className="brand-mark">
              <Radio size={18} />
            </span>
            <span>Live Frame</span>
          </div>
          <div className="session-status">
            <span className={`status-dot ${session.isSpeaking ? "speaking" : ""}`} />
            {transportStatus}
          </div>
        </div>

        <RemoteCharacterVideo
          asset={playableAsset}
          state={session.visualState}
          displayName={manifest?.displayName || "Neko Gamer"}
          mouthLevel={ttsMouthLevel}
          onStateVisible={markVisualStateVisible}
        />

        <div className="subtitle-stack" aria-live="polite">
          {session.userTranscript ? (
            <p className="subtitle user-line">你：{session.userTranscript}</p>
          ) : null}
          {session.assistantSubtitle ? (
            <p className="subtitle assistant-line">{session.assistantSubtitle}</p>
          ) : null}
        </div>

        <LocalPreview stream={localStream} isCameraOn={isCameraOn} audioLevel={audioLevel} />

        <form className="composer" onSubmit={handleSubmit}>
          <input
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder="输入一句话，模拟用户语音"
            disabled={session.isSpeaking}
          />
          <button
            type="submit"
            className="primary-action"
            disabled={session.isSpeaking || !input.trim() || (!isElevenLabsReady && !allowBrowserTtsFallback)}
          >
            <Send size={18} />
            发送
          </button>
        </form>

        <div className="call-controls" aria-label="通话控制">
          <button type="button" onClick={() => setIsMicOn((value) => !value)} title="麦克风">
            {isMicOn ? <Mic size={20} /> : <MicOff size={20} />}
          </button>
          <button type="button" onClick={toggleCamera} title="摄像头">
            <Camera size={20} />
          </button>
          <button type="button" onClick={interrupt} title="打断角色">
            <CirclePause size={20} />
          </button>
          <button type="button" className="danger" onClick={interrupt} title="挂断">
            <PhoneOff size={20} />
          </button>
        </div>
      </section>

      <aside className="control-panel" aria-label="控制面板">
        <div className="panel-section compact">
          <div>
            <h1>角色通话控制台</h1>
            <p>Seedance 状态帧 + ElevenLabs TTS 的本地集成 MVP。</p>
          </div>
          <Sparkles className="panel-icon" size={24} />
        </div>

        <div className="metric-grid">
          <div className="metric">
            <span>当前状态</span>
            <strong>{session.visualState}</strong>
          </div>
          <div className="metric">
            <span>TTS</span>
            <strong>{ttsStatus}</strong>
          </div>
          <div className="metric">
            <span>LLM</span>
            <strong>{latencyMs === null ? llmStatus : `${latencyMs}ms`}</strong>
          </div>
          <div className="metric">
            <span>素材</span>
            <strong>{playableAsset?.kind === "video" ? "视频" : "参考图"}</strong>
          </div>
        </div>

        <StateRail states={visualStates} activeState={session.visualState} onSelect={selectState} />

        <div className="panel-section">
          <h2>
            <Captions size={18} />
            字幕流
          </h2>
          <div className="transcript-box">
            <p>{session.userTranscript || "等待用户输入。"}</p>
            <p>{session.assistantSubtitle || "角色回复会显示在这里。"}</p>
          </div>
        </div>

        <div className="panel-section">
          <h2>
            <Wand2 size={18} />
            生成流水线
          </h2>
          <ol className="pipeline-list">
            <li>编辑 `assets/characters/neko-gamer/seedance-prompts`。</li>
            <li>运行 `npm run seedance:payload` 检查请求。</li>
            <li>运行 `npm run seedance:submit -- --only=idle` 生成单状态。</li>
            <li>运行 `npm run transcode` 同步到 public。</li>
          </ol>
        </div>

        <div className="panel-section">
          <h2>
            <Volume2 size={18} />
            快速试播
          </h2>
          <div className="quick-actions">
            <button
              type="button"
              disabled={!isElevenLabsReady && !allowBrowserTtsFallback}
              onClick={() => void playElevenLabsOrFallback("我在这里，刚刚正在打游戏。", "neutral")}
            >
              <Play size={16} />
              普通
            </button>
            <button
              type="button"
              disabled={!isElevenLabsReady && !allowBrowserTtsFallback}
              onClick={() => void playElevenLabsOrFallback("好耶，这局感觉要赢啦。", "happy")}
            >
              <Play size={16} />
              开心
            </button>
            <button
              type="button"
              disabled={!isElevenLabsReady && !allowBrowserTtsFallback}
              onClick={() => void playElevenLabsOrFallback("别急，我会慢慢陪你说。", "soft")}
            >
              <Play size={16} />
              温柔
            </button>
          </div>
        </div>
      </aside>
    </main>
  );
}

function readStoredAccessToken() {
  try {
    return window.sessionStorage.getItem(accessTokenStorageKey) || "";
  } catch {
    return "";
  }
}

function writeStoredAccessToken(token: string) {
  try {
    if (token) {
      window.sessionStorage.setItem(accessTokenStorageKey, token);
    }
  } catch {
    // Session storage may be unavailable in strict browser modes.
  }
}

function clearStoredAccessToken() {
  try {
    window.sessionStorage.removeItem(accessTokenStorageKey);
  } catch {
    // Session storage may be unavailable in strict browser modes.
  }
}

function VideoPreloadCache({ videos }: { videos: VideoPreloadItem[] }) {
  if (videos.length === 0) return null;

  return (
    <div className="video-preload-cache" aria-hidden="true">
      {videos.map((video) => (
        <video key={`${video.state}:${video.src}`} data-preload-state={video.state} preload="auto" muted playsInline>
          <source src={video.src} type="video/webm" />
          {video.fallback ? <source src={video.fallback} type="video/mp4" /> : null}
        </video>
      ))}
    </div>
  );
}
